import { useState, useEffect, useMemo } from "react";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Users,
  IndianRupee,
  MapPin,
  Sparkles,
  RotateCcw,
  Search,
  Crosshair,
  Loader2,
} from "lucide-react";

/**
 * SIP-Calculator–style filter card with:
 *  - Followers (stops-based slider — non-linear scale)
 *  - Budget (stops-based slider)
 *  - Area = Radius slider (km from a center location)
 *  - Category (dropdown)
 */

// --- FOLLOWERS scale ---
// Non-linear stops — tighter at the low end, looser at the top
export const FOLLOWERS_STOPS = [
  0, 1000, 5000, 10000, 20000, 50000, 100000, 200000, 500000, 1000000, 5000000, 10000000,
];
export const FOLLOWERS_MIN = FOLLOWERS_STOPS[0];
export const FOLLOWERS_MAX = FOLLOWERS_STOPS[FOLLOWERS_STOPS.length - 1];

// --- BUDGET scale (₹/hour) ---
export const BUDGET_STOPS = [0, 1000, 5000, 10000, 25000, 50000, 100000, 200000];
export const BUDGET_MIN = BUDGET_STOPS[0];
export const BUDGET_MAX = BUDGET_STOPS[BUDGET_STOPS.length - 1];

// --- RADIUS scale (km) ---
export const RADIUS_STOPS = [5, 10, 20, 50, 100, 200, 500];
export const RADIUS_MIN = RADIUS_STOPS[0];
export const RADIUS_MAX = RADIUS_STOPS[RADIUS_STOPS.length - 1];

// Format helpers
const fmtFollowers = (n) => {
  if (n >= 10_000_000) return "10M+";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`;
  if (n >= 100_000) return `${Math.round(n / 1000)}K`;
  if (n >= 1000) return `${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}K`;
  return `${n}`;
};
const fmtBudget = (n) => {
  if (n >= 200_000) return "₹2L+";
  if (n >= 100_000) return `₹${(n / 100_000).toFixed(n % 100_000 === 0 ? 0 : 1)}L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}K`;
  return `₹${n}`;
};

// Helpers: map between stop-array index <-> actual value (clamp + nearest)
const valueToIndex = (value, stops) => {
  // exact match wins, else nearest stop
  let bestIdx = 0;
  let bestDiff = Math.abs(stops[0] - value);
  for (let i = 1; i < stops.length; i++) {
    const d = Math.abs(stops[i] - value);
    if (d < bestDiff) {
      bestDiff = d;
      bestIdx = i;
    }
  }
  return bestIdx;
};
const indexToValue = (idx, stops) => stops[Math.max(0, Math.min(stops.length - 1, idx))];

const SipStyleFilter = ({
  categories = [],
  selectedCategory = "all",
  onCategoryChange = () => {},
  filters = {},
  onApply = () => {},
  onReset = () => {},
  variant = "light",
  resultCount = null,
  title = "Find your perfect star",
  showCategory = true,
  compact = false,
  // Geo
  centerLocation = null, // { lat, lng, label } — if provided, radius slider is auto-enabled
  onRequestLocation = null, // optional callback when user clicks "Detect my location"
  detectingLocation = false,
}) => {
  // Local state — keeps slider snappy
  const [followersIdx, setFollowersIdx] = useState([
    valueToIndex(typeof filters.minFollowers === "number" ? filters.minFollowers : FOLLOWERS_MIN, FOLLOWERS_STOPS),
    valueToIndex(typeof filters.maxFollowers === "number" ? filters.maxFollowers : FOLLOWERS_MAX, FOLLOWERS_STOPS),
  ]);
  const [budgetIdx, setBudgetIdx] = useState([
    valueToIndex(typeof filters.minBudget === "number" ? filters.minBudget : BUDGET_MIN, BUDGET_STOPS),
    valueToIndex(typeof filters.maxBudget === "number" ? filters.maxBudget : BUDGET_MAX, BUDGET_STOPS),
  ]);
  const [radius, setRadius] = useState(
    typeof filters.radiusKm === "number" && filters.radiusKm > 0 ? filters.radiusKm : 20
  );

  // Sync from parent if it changes externally (e.g., Reset)
  useEffect(() => {
    setFollowersIdx([
      valueToIndex(typeof filters.minFollowers === "number" ? filters.minFollowers : FOLLOWERS_MIN, FOLLOWERS_STOPS),
      valueToIndex(typeof filters.maxFollowers === "number" ? filters.maxFollowers : FOLLOWERS_MAX, FOLLOWERS_STOPS),
    ]);
    setBudgetIdx([
      valueToIndex(typeof filters.minBudget === "number" ? filters.minBudget : BUDGET_MIN, BUDGET_STOPS),
      valueToIndex(typeof filters.maxBudget === "number" ? filters.maxBudget : BUDGET_MAX, BUDGET_STOPS),
    ]);
    setRadius(typeof filters.radiusKm === "number" && filters.radiusKm > 0 ? filters.radiusKm : 20);
  }, [filters.minFollowers, filters.maxFollowers, filters.minBudget, filters.maxBudget, filters.radiusKm]);

  const handleReset = () => {
    setFollowersIdx([0, FOLLOWERS_STOPS.length - 1]);
    setBudgetIdx([0, BUDGET_STOPS.length - 1]);
    setRadius(20);
    onReset();
  };

  const handleApply = () => {
    onApply({
      minFollowers: indexToValue(followersIdx[0], FOLLOWERS_STOPS),
      maxFollowers: indexToValue(followersIdx[1], FOLLOWERS_STOPS),
      minBudget: indexToValue(budgetIdx[0], BUDGET_STOPS),
      maxBudget: indexToValue(budgetIdx[1], BUDGET_STOPS),
      radiusKm: centerLocation ? radius : null,
    });
  };

  const isDark = variant === "dark";

  // Active filter count (for badge)
  const activeCount = useMemo(() => {
    let n = 0;
    if (followersIdx[0] !== 0 || followersIdx[1] !== FOLLOWERS_STOPS.length - 1) n++;
    if (budgetIdx[0] !== 0 || budgetIdx[1] !== BUDGET_STOPS.length - 1) n++;
    if (selectedCategory && selectedCategory !== "all") n++;
    if (centerLocation && radius !== 20) n++;
    return n;
  }, [followersIdx, budgetIdx, radius, centerLocation, selectedCategory]);

  const cardClasses = isDark
    ? "bg-gradient-to-br from-[#1E1B3A] via-[#2A1F4D] to-[#1A1530] text-white shadow-2xl"
    : "bg-white text-[#1E293B] shadow-xl";

  const followersLabel = `${fmtFollowers(indexToValue(followersIdx[0], FOLLOWERS_STOPS))} – ${fmtFollowers(indexToValue(followersIdx[1], FOLLOWERS_STOPS))}`;
  const budgetLabel = `${fmtBudget(indexToValue(budgetIdx[0], BUDGET_STOPS))} – ${fmtBudget(indexToValue(budgetIdx[1], BUDGET_STOPS))}`;

  return (
    <div
      className={`rounded-3xl ${cardClasses} ${compact ? "p-5" : "p-6 md:p-8"} border ${
        isDark ? "border-white/10" : "border-gray-100"
      } relative overflow-hidden`}
      data-testid="sip-style-filter"
    >
      {/* Decorative gradient blobs */}
      <div
        className={`absolute -top-16 -right-16 w-48 h-48 rounded-full blur-3xl pointer-events-none ${
          isDark ? "bg-[#FF69B4]/20" : "bg-[#FF69B4]/15"
        }`}
      />
      <div
        className={`absolute -bottom-16 -left-16 w-48 h-48 rounded-full blur-3xl pointer-events-none ${
          isDark ? "bg-[#7B2FBE]/30" : "bg-[#7B2FBE]/10"
        }`}
      />

      <div className="relative">
        {/* Header */}
        <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <div
              className={`w-9 h-9 rounded-xl flex items-center justify-center ${
                isDark ? "bg-white/10" : "bg-gradient-to-br from-[#FFE4EC] to-[#F3E8FF]"
              }`}
            >
              <Sparkles className={`w-5 h-5 ${isDark ? "text-[#FF69B4]" : "text-[#FF69B4]"}`} />
            </div>
            <div>
              <h3 className={`font-outfit font-bold ${compact ? "text-lg" : "text-xl"} leading-tight`}>
                {title}
              </h3>
              <p className={`text-xs ${isDark ? "text-white/60" : "text-gray-500"}`}>
                Drag the sliders to set your range
              </p>
            </div>
          </div>

          {activeCount > 0 && (
            <button
              onClick={handleReset}
              className={`flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-full transition ${
                isDark
                  ? "bg-white/10 hover:bg-white/20 text-white"
                  : "bg-gray-100 hover:bg-gray-200 text-gray-700"
              }`}
              data-testid="sip-filter-reset-btn"
            >
              <RotateCcw className="w-3 h-3" />
              Reset ({activeCount})
            </button>
          )}
        </div>

        {/* CATEGORY DROPDOWN */}
        {showCategory && categories.length > 0 && (
          <div className="mb-5">
            <label className={`text-xs font-semibold mb-1.5 uppercase tracking-wide block ${
              isDark ? "text-white/60" : "text-gray-500"
            }`}>
              Category
            </label>
            <Select value={selectedCategory} onValueChange={onCategoryChange}>
              <SelectTrigger
                className={`h-11 rounded-xl font-medium ${
                  isDark
                    ? "bg-white/5 border-white/10 text-white hover:bg-white/10 focus:ring-[#FF69B4]"
                    : "bg-gray-50 border-gray-200 text-gray-900"
                }`}
                data-testid="sip-category-trigger"
              >
                <SelectValue placeholder="All categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  <span className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    All categories
                  </span>
                </SelectItem>
                {categories.map((cat) => {
                  if (cat.id === "all") return null;
                  const Icon = cat.icon;
                  return (
                    <SelectItem key={cat.id} value={cat.id} data-testid={`sip-category-${cat.id}`}>
                      <span className="flex items-center gap-2">
                        {Icon ? (
                          <Icon className="w-4 h-4" style={{ color: cat.color || "#FF69B4" }} />
                        ) : null}
                        {cat.name}
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Two-column on desktop / stacked on mobile */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {/* FOLLOWERS slider (stops) */}
          <div className={`rounded-2xl p-4 ${
            isDark ? "bg-white/5 border border-white/10" : "bg-gradient-to-br from-pink-50 to-pink-100/40 border border-pink-100"
          }`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Users className={`w-4 h-4 ${isDark ? "text-[#FF69B4]" : "text-[#FF69B4]"}`} />
                <span className={`text-sm font-semibold ${isDark ? "text-white" : "text-gray-800"}`}>
                  Followers
                </span>
              </div>
              <div className={`px-3 py-1 rounded-full text-xs font-bold ${
                isDark ? "bg-[#FF69B4]/20 text-[#FFB6D5]" : "bg-white text-[#FF69B4] shadow-sm"
              }`}>
                {followersLabel}
              </div>
            </div>

            <Slider
              value={followersIdx}
              min={0}
              max={FOLLOWERS_STOPS.length - 1}
              step={1}
              minStepsBetweenThumbs={0}
              onValueChange={(v) => setFollowersIdx(v)}
              variant="pink"
              className="my-4"
              data-testid="sip-followers-slider"
            />

            <div className={`flex justify-between text-[10px] font-medium ${
              isDark ? "text-white/50" : "text-gray-400"
            }`}>
              <span>0</span>
              <span>1K</span>
              <span>10K</span>
              <span>100K</span>
              <span>1M</span>
              <span>10M+</span>
            </div>
          </div>

          {/* BUDGET slider (stops) */}
          <div className={`rounded-2xl p-4 ${
            isDark ? "bg-white/5 border border-white/10" : "bg-gradient-to-br from-purple-50 to-blue-50 border border-purple-100"
          }`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <IndianRupee className={`w-4 h-4 ${isDark ? "text-[#87CEEB]" : "text-[#7B2FBE]"}`} />
                <span className={`text-sm font-semibold ${isDark ? "text-white" : "text-gray-800"}`}>
                  Budget
                </span>
              </div>
              <div className={`px-3 py-1 rounded-full text-xs font-bold ${
                isDark ? "bg-[#87CEEB]/20 text-[#BEE5F4]" : "bg-white text-[#7B2FBE] shadow-sm"
              }`}>
                {budgetLabel}
              </div>
            </div>

            <Slider
              value={budgetIdx}
              min={0}
              max={BUDGET_STOPS.length - 1}
              step={1}
              minStepsBetweenThumbs={0}
              onValueChange={(v) => setBudgetIdx(v)}
              variant="blue"
              className="my-4"
              data-testid="sip-budget-slider"
            />

            <div className={`flex justify-between text-[10px] font-medium ${
              isDark ? "text-white/50" : "text-gray-400"
            }`}>
              <span>₹0</span>
              <span>₹1K</span>
              <span>₹5K</span>
              <span>₹10K</span>
              <span>₹25K</span>
              <span>₹50K</span>
              <span>₹1L</span>
              <span>₹2L</span>
            </div>
          </div>
        </div>

        {/* AREA = RADIUS slider (km from center location) */}
        <div className={`mt-5 rounded-2xl p-4 ${
          isDark ? "bg-white/5 border border-white/10" : "bg-gradient-to-br from-orange-50 to-yellow-50 border border-orange-100"
        }`}>
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <MapPin className={`w-4 h-4 ${isDark ? "text-[#FFD700]" : "text-[#FF6B00]"}`} />
              <span className={`text-sm font-semibold ${isDark ? "text-white" : "text-gray-800"}`}>
                Search radius
              </span>
            </div>
            {centerLocation ? (
              <div className={`px-3 py-1 rounded-full text-xs font-bold ${
                isDark ? "bg-[#FFD700]/20 text-[#FFE680]" : "bg-white text-[#FF6B00] shadow-sm"
              }`}>
                {radius} km from {centerLocation.label || "your location"}
              </div>
            ) : (
              <span className={`text-xs ${isDark ? "text-white/50" : "text-gray-400"}`}>
                Set a location to enable
              </span>
            )}
          </div>

          {centerLocation ? (
            <>
              <Slider
                value={[radius]}
                min={RADIUS_MIN}
                max={RADIUS_MAX}
                step={5}
                onValueChange={(v) => setRadius(v[0])}
                variant="pink"
                className="my-4"
                data-testid="sip-radius-slider"
              />
              <div className={`flex justify-between text-[10px] font-medium ${
                isDark ? "text-white/50" : "text-gray-400"
              }`}>
                <span>5 km</span>
                <span>20 km</span>
                <span>50 km</span>
                <span>100 km</span>
                <span>200 km</span>
                <span>500 km</span>
              </div>
            </>
          ) : (
            <div className="mt-3">
              <p className={`text-xs mb-2 ${isDark ? "text-white/60" : "text-gray-500"}`}>
                Allow location to search by radius around you.
              </p>
              <Button
                type="button"
                onClick={() => onRequestLocation && onRequestLocation()}
                disabled={detectingLocation || !onRequestLocation}
                className={`rounded-full h-9 text-xs font-semibold ${
                  isDark
                    ? "bg-white text-[#1E1B3A] hover:bg-white/90"
                    : "bg-gradient-to-r from-[#FF6B00] to-[#FF8E53] text-white hover:opacity-90"
                }`}
                data-testid="sip-detect-location-btn"
              >
                {detectingLocation ? (
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                ) : (
                  <Crosshair className="w-3.5 h-3.5 mr-1.5" />
                )}
                {detectingLocation ? "Detecting..." : "Detect my location"}
              </Button>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className={`mt-6 flex items-center justify-between gap-3 pt-4 border-t ${
          isDark ? "border-white/10" : "border-gray-100"
        }`}>
          <div className={`text-xs ${isDark ? "text-white/60" : "text-gray-500"}`}>
            {resultCount !== null ? (
              <span>
                <span className={`font-bold ${isDark ? "text-white" : "text-[#FF69B4]"}`}>
                  {resultCount}
                </span>{" "}
                {resultCount === 1 ? "star" : "stars"} match your filters
              </span>
            ) : (
              <span>Adjust to find your perfect match</span>
            )}
          </div>

          <Button
            onClick={handleApply}
            className="bg-gradient-to-r from-[#FF69B4] to-[#7B2FBE] hover:opacity-90 text-white rounded-full px-6 h-10 font-semibold shadow-lg"
            data-testid="sip-filter-apply-btn"
          >
            <Search className="w-4 h-4 mr-1.5" />
            Apply Filters
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SipStyleFilter;
